import React from 'react';
import type { Leg, StayOption, TransportOption, TravelOption } from './data';

export const timeOf = (iso: string) => iso.slice(11, 16);
const dayOf = (iso: string) => iso.slice(0, 10);

export const ukDate = (iso: string, weekday = true) =>
  new Date(`${dayOf(iso)}T12:00:00Z`)
    .toLocaleDateString('en-GB', {
      ...(weekday ? { weekday: 'short' } : {}),
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    })
    .replace(',', '');

export const duration = (min: number) =>
  `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;

export const money = (n: number, currency: string, exact = false) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: exact && n % 1 ? 2 : 0,
    maximumFractionDigits: exact ? 2 : 0,
  }).format(n);

export const plusDays = (dep: string, arr: string) =>
  Math.round(
    (new Date(`${dayOf(arr)}T00:00:00Z`).getTime() -
      new Date(`${dayOf(dep)}T00:00:00Z`).getTime()) /
      86400000,
  );

export const modeOf = (o: TravelOption) => o.mode;

export function ctaLabel(o: TravelOption) {
  if (o.kind === 'flight' && o.mode === 'select') return 'Details and options';
  if (modeOf(o) === 'agent') return 'Ask Dream to book';
  return 'Book now';
}

export const ctaHelp = (o: TravelOption) =>
  o.kind === 'flight' && o.mode === 'select'
    ? 'Choose your outbound flight first'
    : null;

export function priceNote(o: TravelOption) {
  if (o.kind === 'hotel')
    return `${o.nights} ${o.nights === 1 ? 'night' : 'nights'}`;
  if (o.legs.length > 1) return 'return';
  return o.mode === 'select' ? 'from, return' : 'one way';
}

export function carriersLabel(o: TransportOption) {
  return Array.from(
    new Set(o.legs.flatMap((l) => l.segments.map((s) => s.carrierName))),
  ).join(' + ');
}

export function CarrierMark({
  code,
  name,
  train,
}: {
  code: string;
  name: string;
  train?: boolean;
}) {
  const [failed, setFailed] = React.useState(false);
  if (train || failed) {
    return (
      <span className='to-mark to-mark--text' title={name}>
        {code}
      </span>
    );
  }
  return (
    <img
      className='to-mark'
      src={`https://assets.duffel.com/img/airlines/for-light-background/full-color-logo/${code}.svg`}
      alt={name}
      title={name}
      onError={() => setFailed(true)}
    />
  );
}

const shortStation = (name?: string) =>
  name?.replace(/^(London|Paris|Lyon) /, '') ?? '';

export function PlaneGlyph() {
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true'>
      <path
        d='M21 15.5v-1.7l-8-5V3.5a1.5 1.5 0 0 0-3 0v5.3l-8 5v1.7l8-2.5v5.2l-2 1.5V21l3.5-1 3.5 1v-1.3l-2-1.5V13l8 2.5Z'
        fill='currentColor'
      />
    </svg>
  );
}

export function SideTrainGlyph() {
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true'>
      <path
        d='M2 7.5A1.5 1.5 0 0 1 3.5 6H15c3.9 0 7 2.9 7 6.5V16a1 1 0 0 1-1 1H3.5A1.5 1.5 0 0 1 2 15.5v-8ZM4 8.5V12h3.5V8.5H4Zm5.5 0V12H13V8.5H9.5Zm5.5 0V12h4.8A5 5 0 0 0 15 8.5ZM3 18.5h18V20H3v-1.5Z'
        fill='currentColor'
      />
    </svg>
  );
}

export function LegRow({
  leg,
  train,
  carrier = true,
}: {
  leg: Leg;
  train: boolean;
  carrier?: boolean;
}) {
  const stops = leg.segments.slice(0, -1);
  const days = plusDays(leg.dep, leg.arr);
  const first = leg.segments[0];
  const last = leg.segments[leg.segments.length - 1];
  const via = stops
    .map((s) => (train ? shortStation(s.toName ?? s.to) : s.to))
    .join(', ');
  const n = stops.length;
  const word = train
    ? n > 1
      ? 'changes'
      : 'change'
    : n > 1
      ? 'stops'
      : 'stop';
  return (
    <div className={`to-leg${carrier ? '' : ' to-leg--bare'}`}>
      {carrier && (
        <span className='to-leg__carrier'>
          <CarrierMark
            code={first.carrierCode}
            name={first.carrierName}
            train={train}
          />
          <span className='to-desk'>{first.carrierName}</span>
        </span>
      )}
      <div className='to-leg__end'>
        <strong>{timeOf(leg.dep)}</strong>
        <span>{train ? shortStation(first.fromName) : leg.from}</span>
      </div>
      <div className='to-leg__mid'>
        <span>{duration(leg.durationMin)}</span>
        <span className='to-leg__line'>
          {stops.map((s, i) => (
            <i
              key={s.to + i}
              className='to-leg__dot'
              style={{ left: `${((i + 1) / (n + 1)) * 100}%` }}
            />
          ))}
          <span
            className={`to-leg__glyph${train ? '' : ' to-leg__glyph--plane'}`}
          >
            {train ? <SideTrainGlyph /> : <PlaneGlyph />}
          </span>
        </span>
        {n === 0 ? (
          <span className='to-leg__direct'>Direct</span>
        ) : (
          <span className='to-leg__stops'>
            {n} {word} <b>{via}</b>
          </span>
        )}
      </div>
      <div className='to-leg__end to-leg__end--arr'>
        <strong>
          {timeOf(leg.arr)}
          {days > 0 && <sup>+{days}</sup>}
        </strong>
        <span>{train ? shortStation(last.toName) : leg.to}</span>
      </div>
    </div>
  );
}

export function Price({ o, stale }: { o: TravelOption; stale?: boolean }) {
  return (
    <div className='to-price'>
      <strong>
        {money(o.price, o.currency)}
        {stale && '*'}
      </strong>
      <span>{priceNote(o)}</span>
    </div>
  );
}

function TransportBody({ o }: { o: TransportOption }) {
  const train = o.kind === 'train';
  if (train && o.legs.length === 0) {
    return (
      <div className='to-row'>
        <div className='to-row__body'>
          <h3>{o.title}</h3>
          {o.blurb && <span className='to-muted'>{o.blurb}</span>}
          {o.fares[0]?.name && o.fares[0].name !== 'Train ticket' && (
            <span className='to-muted'>
              <strong>{o.fares[0].name}</strong>
              {o.fares[0].nameDetail && <> {o.fares[0].nameDetail}</>}
            </span>
          )}
          {o.fares[0]?.conditions && (
            <span className='to-muted'>{o.fares[0].conditions}</span>
          )}
        </div>
      </div>
    );
  }
  const marks = Array.from(
    new Map(
      o.legs.flatMap((l) => l.segments).map((s) => [s.carrierCode, s]),
    ).values(),
  ).slice(0, 2);
  return (
    <>
      <div className='to-card__carrier'>
        {marks.map((s) => (
          <CarrierMark
            key={s.carrierCode}
            code={s.carrierCode}
            name={s.carrierName}
            train={train}
          />
        ))}
        <span>{carriersLabel(o)}</span>
        {o.kind === 'flight' &&
          o.mode === 'instant' &&
          o.pairedSearch &&
          o.legs.length === 2 && (
            <span className='to-tag'>Book with Dream</span>
          )}
      </div>
      {train && o.fares[0]?.name && o.fares[0].name !== 'Train ticket' && (
        <span className='to-muted to-card__note'>
          <strong>{o.fares[0].name}</strong>
          {o.fares[0].nameDetail && <> {o.fares[0].nameDetail}</>}
        </span>
      )}
      {o.legs.map((leg) => (
        <LegRow
          key={leg.label + leg.dep}
          leg={leg}
          train={train}
          carrier={false}
        />
      ))}
      {o.kind === 'flight' && o.blurb && (
        <span className='to-muted to-card__note'>{o.blurb}</span>
      )}
    </>
  );
}

export function ratingWord(score: number, outOf: number) {
  const s = (score / outOf) * 10;
  if (s >= 9) return 'Exceptional';
  if (s >= 8.5) return 'Excellent';
  if (s >= 8) return 'Very good';
  if (s >= 7) return 'Good';
  return 'Rated';
}

export function Rating({ o }: { o: StayOption }) {
  if (!o.rating) return null;
  const score = (o.rating.score / o.rating.outOf) * 10;
  return (
    <span className='to-rating'>
      <b>{score.toFixed(1)}</b>
      {ratingWord(o.rating.score, o.rating.outOf)}
    </span>
  );
}

function StayBody({ o }: { o: StayOption }) {
  const rate = o.rates[0];
  return (
    <div className='to-row'>
      <div
        className='to-thumb'
        style={{
          backgroundImage: o.photos[0] ? `url("${o.photos[0]}")` : undefined,
        }}
      />
      <div className='to-row__body'>
        <h3>{o.title}</h3>
        {o.blurb && <span className='to-muted to-card__note'>{o.blurb}</span>}
        <Rating o={o} />
        {o.address && <span className='to-muted to-desk'>{o.address}</span>}
        {rate && <span className='to-muted to-desk'>{rate.room}</span>}
        {rate?.refundable && <span className='to-good'>Free cancellation</span>}
      </div>
    </div>
  );
}

export function OptionCard({
  option,
  onOpen,
  onBook,
  picked,
  bookingDisabled = false,
  done,
  bookingStatus,
  stale,
}: {
  option: TravelOption;
  onOpen: () => void;
  onBook: () => void;
  picked?: boolean;
  bookingDisabled?: boolean;
  done?: string | null;
  bookingStatus?: string | null;
  stale?: boolean;
}) {
  const pair = option.kind === 'flight' && option.mode === 'select';
  return (
    <div
      className={`to-card${picked ? ' to-card--picked' : ''}${option.kind === 'hotel' ? ' to-card--stay' : ''}`}
    >
      <button
        type='button'
        className='to-card__tap'
        onClick={pair && !bookingDisabled ? onBook : onOpen}
      >
        {option.kind === 'hotel' ? (
          <StayBody o={option} />
        ) : (
          <TransportBody o={option} />
        )}
      </button>
      <div className='to-card__side'>
        <Price o={option} stale={stale} />
        {done && <p className='to-done'>{done}</p>}
        <div className='to-card__btns'>
          <button
            type='button'
            className='to-btn to-btn--primary'
            onClick={pair && !bookingDisabled ? onBook : onOpen}
          >
            Details and options
          </button>
        </div>
      </div>
      {bookingStatus && <p className='to-booking-status'>{bookingStatus}</p>}
    </div>
  );
}

export function SkeletonCard({
  kind = 'flight',
  paired = false,
}: {
  kind?: 'flight' | 'hotel' | 'train';
  paired?: boolean;
}) {
  return (
    <div
      className={`to-card to-skeleton to-skeleton--${kind}${paired ? ' to-skeleton--paired' : ''}`}
      aria-hidden='true'
    >
      <i />
      <i />
    </div>
  );
}
